import { useEffect, useState } from "react";
import { X } from "lucide-react";
import axios from "axios";
import { openRazorpayCheckout } from "../../lib/razorpay";

const API = process.env.REACT_APP_BACKEND_URL;

const L = "block text-xs tracking-[0.16em] uppercase text-slate-400 mb-2";
const IN = "w-full bg-white/[0.04] border border-white/15 focus:border-[#C9A84C] outline-none px-4 py-3 text-[15px] text-white placeholder:text-slate-600 transition-colors";

export const PayNow = ({ product }) => {
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState("");
  const [err, setErr] = useState("");
  const [rzpPublic, setRzpPublic] = useState(false);
  const [f, setF] = useState({ name: "", email: "", phone: "", amount: "", notes: "" });
  const set = (k) => (e) => setF((p) => ({ ...p, [k]: e.target.value }));

  useEffect(() => {
    if (!open) return;
    axios.get(`${API}/api/payments/config-status`).then((r) => { setRzpPublic(!!r.data.razorpay_public); }).catch(() => {});
  }, [open]);

  const start = async (gateway) => {
    setErr("");
    setBusy(gateway);
    try {
      const res = await axios.post(`${API}/api/payments/initiate`, {
        name: f.name, email: f.email, phone: f.phone, product,
        amount: parseFloat(f.amount) || 0, notes: f.notes, gateway,
      });
      if (res.data.razorpay) {
        await openRazorpayCheckout({
          API, txnid: res.data.txnid,
          onPaid: () => window.location.assign(`/payment/success?txnid=${res.data.txnid}`),
          onError: (m) => { setErr(m); setBusy(""); },
        });
        return;
      }
      if (res.data.redirect_url) {
        window.location.assign(res.data.redirect_url);
        return;
      }
      const { payment_url, form } = res.data;
      // Browser POST is intentional: PayU receives the hosted-checkout form directly.
      const el = document.createElement("form");
      el.method = "POST";
      el.action = payment_url;
      Object.entries(form).forEach(([k, v]) => {
        const i = document.createElement("input");
        i.type = "hidden";
        i.name = k;
        i.value = String(v ?? "");
        el.appendChild(i);
      });
      document.body.appendChild(el);
      el.submit();
    } catch (ex) {
      setErr(ex.response?.data?.detail || "Could not start the payment. Please try again.");
      setBusy("");
    }
  };

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        className="w-full bg-[#C9A84C] hover:bg-[#dbb95c] text-[#0A1628] font-semibold text-sm tracking-[0.14em] uppercase px-6 py-4 transition-all duration-300 hover:-translate-y-0.5"
        data-testid="pay-now-button"
      >
        Pay Now
      </button>

      {open && (
        <div data-lenis-prevent className="fixed inset-0 z-[90] bg-black/75 flex items-end sm:items-center justify-center p-0 sm:p-6" data-testid="pay-modal">
          <div className="w-full sm:max-w-md bg-[#0A1628] border border-[#C9A84C]/40 p-6 sm:p-7 max-h-[92vh] overflow-y-auto">
            <div className="flex items-start justify-between mb-1">
              <h2 className="font-marcellus text-xl text-white">Pay Online</h2>
              <button type="button" onClick={() => setOpen(false)} className="text-slate-400 hover:text-white p-1" aria-label="Close" data-testid="pay-close"><X size={19} /></button>
            </div>
            <p className="text-xs text-slate-500 mb-5">Secure payment via Razorpay or PayU. You will be redirected to the gateway&apos;s checkout page.</p>
            <form onSubmit={(e) => e.preventDefault()} className="space-y-4">
              <div><label className={L} htmlFor="pay-name">Customer Name</label><input id="pay-name" data-testid="pay-name" className={IN} value={f.name} onChange={set("name")} required /></div>
              <div><label className={L} htmlFor="pay-email">Customer Email</label><input id="pay-email" data-testid="pay-email" type="email" className={IN} value={f.email} onChange={set("email")} required /></div>
              <div><label className={L} htmlFor="pay-phone">Customer Phone</label><input id="pay-phone" data-testid="pay-phone" type="tel" className={IN} value={f.phone} onChange={set("phone")} placeholder="10-digit mobile" required /></div>
              <div><label className={L} htmlFor="pay-product">Product / Service</label><input id="pay-product" data-testid="pay-product" className={`${IN} opacity-70`} value={product} readOnly /></div>
              <div><label className={L} htmlFor="pay-amount">Amount (₹)</label><input id="pay-amount" data-testid="pay-amount" type="number" min="1" max="1000000" step="0.01" className={IN} value={f.amount} onChange={set("amount")} placeholder="Enter amount as per your quote / invoice" required /></div>
              <div><label className={L} htmlFor="pay-notes">Notes (optional)</label><textarea id="pay-notes" data-testid="pay-notes" rows={2} className={IN} value={f.notes} onChange={set("notes")} placeholder="Invoice number, site details…" /></div>
              {err && <p className="text-[12px] text-red-400" data-testid="pay-error">{err}</p>}
              <div className={rzpPublic ? "grid gap-3 sm:grid-cols-2" : ""}>
                {rzpPublic && (
                  <button type="submit" disabled={!!busy} onClick={() => start("razorpay")} className="w-full bg-[#C9A84C] hover:bg-[#dbb95c] disabled:opacity-60 text-[#0A1628] font-bold tracking-wide text-sm px-4 py-4 transition-colors" data-testid="pay-submit-razorpay">
                    {busy === "razorpay" ? "Opening…" : "Pay with Razorpay"}
                  </button>
                )}
                <button type="submit" disabled={!!busy} onClick={() => start("payu")} className="w-full bg-[#C9A84C] hover:bg-[#dbb95c] disabled:opacity-60 text-[#0A1628] font-bold tracking-wide text-sm px-4 py-4 transition-colors" data-testid="pay-submit-payu">
                  {busy === "payu" ? "Redirecting…" : "Pay with PayU"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </>
  );
};
